<script>
import ContentBox from '@/components/ui/ContentBox';
import DirectoryBrowser from '@/components/file-browser/DirectoryBrowser';
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import SidebarLink from '@/components/layouts/dashboard/SidebarLink';
import SidebarLinkGroup from '@/components/layouts/dashboard/SidebarLinkGroup';
import getErrorMessage from '@/modules/server-errors';
import { join, removeBasePath, safeFilename } from '@/modules/file-utilities';

export default {
  name: 'Support',
  components: {
    ContentBox,
    DirectoryBrowser,
    DashboardLayout,
    SidebarLink,
    SidebarLinkGroup
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: this.$t('New Page'),
      breadcrumbs: [],
      cleanState: {},
      form: {
        name: '',
        filename: '',
        extension: '.html',
        templateId: {},
        folder: ''
      },
      hasTemplates: false,
      invalid: [],
      isDisabled: false,
      saving: false,
      site: {},
      statusCode: null,
      templates: []
    };
  },
  created() {
    // Fetch initial data
    Promise.all([
      this.$http.get(`/sites/${this.$route.params.siteId}`),
      this.$http.get(`/sites/${this.$route.params.siteId}/templates`, {
        params: {
          sortBy: 'name',
          limit: 1000
        }
      })
    ])
      .then(res => {
        this.site = res[0].data.site;
        this.templates = res[1].data.templates;
        this.hasTemplates = res[1].data.count > 0;
        this.breadcrumbs = [
          { text: this.$t('Sites'), to: { name: 'Sites' } },
          { text: this.site.name, to: { name: 'Site', params: { siteId: this.site.id } } },
          { text: this.$t('New Page') }
        ];
        if (this.templates.length) {
          this.form.templateId = this.templates[0].id;
          this.form.extension = this.templates[0].extension;
        }
        this.resetCleanState();
        this.statusCode = 200;
        this.$nextTick(() => (this.$refs.name ? this.$refs.name.focus() : null));

        // Bump to the site view if the site is on lockdown or there aren't any templates
        if (this.site.isLocked || !this.hasTemplates) {
          this.$router.push({ name: 'Site', params: { siteId: this.site.id } });
          return;
        }
      })
      .catch(err => (this.statusCode = err.response.status));
  },
  beforeRouteLeave(to, from, next) {
    // Check for unsaved changes
    if (this.isDirty()) {
      this.$confirm(this.$t("Your changes haven't been saved. Are you sure you want to continue?"), {
        title: this.$t('Unsaved Changes'),
        confirmButtonText: this.$t('Continue'),
        cancelButtonText: this.$t('Cancel')
      }).then(() => next());
    } else {
      next();
    }
  },
  methods: {
    // Gets a list of sites for autocomplete
    getSites(search) {
      this.$http
        .get('/sites', {
          params: {
            search,
            limit: 20,
            sortBy: 'name'
          }
        })
        .then(res => (this.sites = res.data.sites))
        .catch(() => (this.sites = []));
    },

    // Prevents unsafe characters from being entered into filename inputs
    handleFilenameChange() {
      this.form.filename = safeFilename(this.form.filename);
    },

    // Generate a filename basded on the name that was entered
    handleNameInput() {
      this.form.filename = safeFilename(this.form.name);
    },

    // Called when a new template is selected
    handleTemplateChange(templateId) {
      const template = this.templates.filter(t => t.id === templateId)[0];
      this.form.extension = template.extension;
    },

    // Tells if the current state is different than the clean state
    isDirty() {
      return JSON.stringify(this.cleanState) !== JSON.stringify(this.form);
    },

    // Sets the clean state to the current state
    resetCleanState() {
      this.cleanState = Object.assign({}, this.form);
    },

    // Opens the folder browser
    openFolderBrowser() {
      this.$refs.folderBrowser.open({
        path: join(this.site.homeFolder, this.form.folder)
      });
    },

    // Closes the folder browser
    closeFolderBrowser() {
      this.$refs.folderBrowser.close();
    },

    // Called when a new folder is selected
    handleFolderSelect(path) {
      this.$refs.folderBrowserPopover.doClose();
      this.form.folder = removeBasePath(path, this.site.homeFolder);
    },

    handleFolderChange() {
      // Remove trailing slashes
      this.form.folder = this.form.folder.replace(/\/+$/, '');
    },

    // Cancels all changes and goes back to the sites page
    handleCancel() {
      this.$router.push({ name: 'Site', params: { siteId: this.site.id } });
    },

    // Create the page
    handleSave() {
      const templateId = this.form.templateId;
      const name = this.form.name;
      const filename = this.form.filename;
      const extension = this.form.extension;
      const path = join(this.form.folder, filename + extension);

      // Check for invalid fields
      this.invalid = [];
      if (!templateId) this.invalid.push('templateId');
      if (!name) this.invalid.push('name');
      if (!filename) this.invalid.push('filename');
      if (this.invalid.length) {
        this.$notify({
          type: 'error',
          title: this.$t('Error'),
          message: this.$t('Please correct the highlighted errors.')
        });
        return;
      }

      this.saving = true;
      this.$http
        .post(`/sites/${this.site.id}/pages`, { name, path, templateId })
        .then(res => {
          const siteId = this.site.id;
          const pageId = res.data.page.id;
          this.isDisabled = true;
          this.resetCleanState();
          this.$router.push({ name: 'EditPage', params: { siteId, pageId } });
        })
        .catch(err => {
          const data = err.response.data;
          this.invalid = Array.isArray(data.invalid) ? data.invalid : [];
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(data.code)
          });
        })
        .finally(() => (this.saving = false));
    }
  }
};
</script>

<template>
  <dashboard-layout :status-code="statusCode" size="small">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- Sidebar -->
    <template slot="sidebar">
      <!-- Help -->
      <sidebar-link-group v-if="$store.state.user.isAdmin" :title="$t('Admin')">
        <sidebar-link
          :text="$t('Manage Templates')"
          :to="{ name: 'Templates', params: { siteId: site.id }}"
          icon="el-icon-fa-code"
        />
      </sidebar-link-group>
    </template>

    <!-- New Page form -->
    <el-form
      :disabled="isDisabled || !hasTemplates"
      label-position="top"
      @submit.native.prevent="handleSave"
    >
      <content-box>
        <fieldset>
          <!-- Name -->
          <el-form-item
            :class="{ 'is-error': invalid.includes('name') }"
            :label="$t('Name')"
          >
            <el-input
              ref="name"
              v-model="form.name"
              type="text"
              prefix-icon="el-icon-fa-tag"
              auto-complete="off"
              @input.native="handleNameInput"
            />
            <div class="text-form-description">
              {{ $t('This is used to reference the page in the CMS.') }}
            </div>
          </el-form-item>

          <!-- Template -->
          <el-form-item
            :class="{ 'is-error': invalid.includes('templateId') }"
            :label="$t('Template')"
          >
            <el-select
              v-model="form.templateId"
              @change="handleTemplateChange"
            >
              <el-option
                v-for="template in templates"
                :key="template.id"
                :label="template.name"
                :value="template.id"
              >
                {{ template.name }}
              </el-option>
            </el-select>
          </el-form-item>

          <el-collapse>
            <el-collapse-item :title="$t('Advanced')">
              <!-- Filename -->
              <el-form-item
                :class="{ 'is-error': invalid.includes('filename') }"
                :label="$t('Filename')"
              >
                <el-input
                  v-model="form.filename"
                  type="text"
                  auto-complete="off"
                  prefix-icon="el-icon-fa-file"
                  @change="handleFilenameChange"
                >
                  <template slot="append">
                    {{ form.extension }}
                  </template>
                </el-input>
              </el-form-item>

              <!-- Folder -->
              <el-form-item :label="$t('Folder')">
                <el-input
                  v-model="form.folder"
                  type="text"
                  auto-complete="off"
                  prefix-icon="el-icon-fa-folder"
                  @change="handleFolderChange"
                >
                  <template slot="append">
                    <el-popover
                      ref="folderBrowserPopover"
                      placement="bottom"
                      trigger="click"
                      @show="openFolderBrowser()"
                      @hide="closeFolderBrowser()"
                    >
                      <el-button slot="reference">
                        {{ $t('Browse') }}
                      </el-button>

                      <directory-browser
                        ref="folderBrowser"
                        :base-path="site.homeFolder"
                        :site="site"
                        :multiple="false"
                        type="popover"
                        @select="handleFolderSelect"
                      />
                    </el-popover>
                  </template>
                </el-input>
                <div class="text-form-description">
                  {{ $t('The page will be created inside this folder on your server.') }}
                </div>
              </el-form-item>
            </el-collapse-item>
          </el-collapse>
        </fieldset>
      </content-box>

      <div class="text-right">
        <!-- Cancel -->
        <el-button @click="handleCancel">
          {{ $t('Cancel') }}
        </el-button>

        <!-- Submit -->
        <el-button :loading="saving" type="primary" native-type="submit">
          {{ $t('Create Page') }}
        </el-button>
      </div>
    </el-form>
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.el-collapse .el-form-item:last-child {
  margin-bottom: 0;
}
</style>
